import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { AppConstants } from '../../../utils/AppConstants';
import { OrganizationConfig } from '../../../models/OrganizationConfig';
import { OrganizationConfigService } from '../../../services/organization-config.service';
import { AlertService } from '../../shared/alert/alert.service';
import { FileUtils } from '../../../services/file-util';

@Component({
  selector: 'app-report-variables',
  standalone: true,
  imports: [
    CommonModule,
    HasPermissionDirective,
    ConfirmAlertComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './report-variables.component.html',
  styleUrl: './report-variables.component.scss',
})
export class ReportVariablesComponent implements OnInit {
  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';

  static readonly LOGO_MAX_SIZE_BYTES = 512 * 1024;

  organizationConfig: OrganizationConfig = {};

  logoPreview?: string;

  constructor(
    private organizationConfigService: OrganizationConfigService,
    private alertService: AlertService,
    private fullSpinnerService: FullSpinnerService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.fullSpinnerService.spinner('');
    this.organizationConfigService.get().subscribe({
      next: (res) => {
        this.organizationConfig = res ?? {};
        this.logoPreview = this.organizationConfig.logo
          ? 'data:image/png;base64,' + this.organizationConfig.logo
          : undefined;
      },
      complete: () => {
        this.fullSpinnerService.spinner(undefined);
      },
      error: () => {
        this.fullSpinnerService.spinner(undefined);
      },
    });
  }

  onSubmit(form: NgForm): void {
    if (!this.organizationConfig.logo) {
      this.alertService.addErrors(
        AppConstants.titleErrorMessage,
        ['Il logo è obbligatorio.'],
        true
      );
      return;
    }
    if (form.valid) {
      this.fullSpinnerService.spinner('');
      this.organizationConfigService.save(this.organizationConfig).subscribe({
        next: (_) => {
          this.alertService.addErrors(
            AppConstants.titleSuccessMessage,
            [AppConstants.successMessage],
            true
          );
        },
        complete: () => {
          this.fullSpinnerService.spinner(undefined);
        },
        error: () => {
          this.alertService.addErrors(
            AppConstants.titleErrorMessage,
            [AppConstants.errorMessage],
            true
          );
          this.fullSpinnerService.spinner(undefined);
        },
      });
    }
  }

  onLogoSelected(event: any): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
    if (file.type !== 'image/png') {
      this.alertService.addErrors(
        AppConstants.titleErrorMessage,
        ['Il logo deve essere un file PNG.'],
        true
      );
      event.target.value = '';
      return;
    }
    if (file.size > ReportVariablesComponent.LOGO_MAX_SIZE_BYTES) {
      this.alertService.addErrors(
        AppConstants.titleErrorMessage,
        ['Il logo non può superare i 512 KB.'],
        true
      );
      event.target.value = '';
      return;
    }
    FileUtils.readFile(file).then((base64) => {
      this.organizationConfig.logo = base64 as string;
      this.logoPreview = 'data:' + file.type + ';base64,' + base64;
    });
  }

  confirmDelete(): void {
    this.showConfirmationModal = true;
    this.confirmationMessage =
      'Sicuro di voler eliminare la configurazione del tuo ente ?';
  }

  cancelDelete(): void {
    this.showConfirmationModal = false;
  }

  delete(): void {
    this.showConfirmationModal = false;
    this.fullSpinnerService.spinner('');
    this.organizationConfigService.delete().subscribe({
      next: (_) => {
        this.alertService.addErrors(
          AppConstants.titleDeleteSuccessMessage,
          [AppConstants.successMessage],
          true
        );
        this.organizationConfig = {};
      },
      complete: () => {
        this.fullSpinnerService.spinner(undefined);
      },
      error: () => {
        this.alertService.addErrors(
          AppConstants.titleErrorMessage,
          [AppConstants.errorMessage],
          true
        );
        this.fullSpinnerService.spinner(undefined);
      },
    });
  }
}
